import { MedicalLeaveForGuardianUpdateCommand } from '@dreryk/egabinet-ezla-frontend-api';
import { VisitMedicalLeaveCreateDraftCommand } from '@dreryk/egabinet-visit-frontend-api';
import { FirstName, LastName, MiddleName } from '@dreryk/person-name-common-api';

import { useCqrsCommand } from '@dreryk/command-query-react-common';
import { RouteLeavingGuard } from '@dreryk/react-components';
import { Choice } from '@dreryk/watermelon';

import { useVisitDocumentContext } from '../../../common/visitDocument/VisitDocumentContext';
import { commonTranslationKeys } from '../../../translations';
import { medicalLeaveDraftCommandTransformer } from '../common/common.utils';

import { GuardianMedicalLeaveFormBean, GuardianMedicalLeaveFormController } from './guardianMedicalLeave.types';

type MedicalLeaveForGuardianUpdateParameters = {
   formData: GuardianMedicalLeaveFormBean;
   externalId: string;
};

type GuardianMedicalLeaveFormRouteLeavingGuardProps = Pick<GuardianMedicalLeaveFormController, 'handleSubmitDraft'> & {
   readonly active: boolean;
};

export const GuardianMedicalLeaveFormRouteLeavingGuard = (
   props: GuardianMedicalLeaveFormRouteLeavingGuardProps
): JSX.Element => {
   const { executeVisitDocumentCommand } = useVisitDocumentContext();
   const { executeAsync: updateMedicalLeaveDraft } = useCqrsCommand<void, MedicalLeaveForGuardianUpdateParameters>(
      (parameters) => {
         const command = new MedicalLeaveForGuardianUpdateCommand();
         // eslint-disable-next-line @typescript-eslint/no-unused-vars
         const { relationCode, leaveDaysCount, birthDate, guardian, ...restVariables } = parameters.formData;

         const { firstName, guardianExternalIdentifier, lastName, middleName } = guardian ?? {};
         command.guardianDetails = {
            firstName: Choice.withUseDefaultIfNull(firstName ? FirstName.of(firstName) : null),
            middleName: Choice.withUseDefaultIfNull(middleName ? MiddleName.of(middleName) : null),
            lastName: Choice.withUseDefaultIfNull(lastName ? LastName.of(lastName) : null),
            guardianExternalIdentifier: Choice.withUseDefaultIfNull(guardianExternalIdentifier ?? null),
            birthDate: Choice.withUseDefaultIfNull(birthDate ?? null),
            relationCode: relationCode,
         };
         medicalLeaveDraftCommandTransformer<MedicalLeaveForGuardianUpdateCommand>(
            parameters.externalId,
            command,
            restVariables
         );
         return command;
      }
   );

   return (
      <RouteLeavingGuard
         title={commonTranslationKeys.draftSaveFailTitle}
         message={commonTranslationKeys.draftSaveFailMessage}
         active={props.active}
         onLeave={props.handleSubmitDraft(async (formData) => {
            return executeVisitDocumentCommand(VisitMedicalLeaveCreateDraftCommand, async (externalId) =>
               updateMedicalLeaveDraft({ formData: formData, externalId: externalId })
            );
         })}
         successMessage={commonTranslationKeys.draftSaveSuccessMessage}
         testId='medicalLeaveRouteLeaving'
      />
   );
};
